import { Search } from "@mui/icons-material";
import { Box, IconButton, Typography } from "@mui/material";
import Divider from "@mui/material/Divider";
import InputBase from "@mui/material/InputBase";
import Paper from "@mui/material/Paper";
import { LoadScriptNext } from "@react-google-maps/api";
import { useState } from "react";
import PlacesAutocomplete, { geocodeByAddress, getLatLng } from "react-places-autocomplete";
import { useDispatch } from "react-redux";
import { useGlobalSnackbar } from "../../context/SnackbarContext";
import { coverageAreaByGeocode } from "../../utils";
import { colors } from "../ui/colors";

export const SearcherAddressPredictiveComponent = ({
  store,
  storeAction,
  onSelectStore,
  onSelectStoreAction,
  label = "Enter a value",
  sx,
  disableLoadScript = false,
  onSelectedLocation,
}) => {
  const dispatch = useDispatch();
  const { showSnackbar } = useGlobalSnackbar();
  const [isResolving, setIsResolving] = useState(false);

  const updateValue = (actionOrSetter, value) => {
    if (!actionOrSetter) return;

    const result = actionOrSetter(value);

    if (typeof result === "function" && actionOrSetter !== result) {
      dispatch(result);
      return;
    }

    if (result && typeof result === "object" && result.type) {
      dispatch(result);
      return;
    }

    actionOrSetter(value);
  };

  const applyAddress = async (address) => {
    const query = (address || "").trim();
    if (!query || isResolving) return;

    setIsResolving(true);
    updateValue(storeAction, query);

    try {
      const results = await geocodeByAddress(query);
      if (!results?.[0]) {
        throw new Error("NO_GEOCODE_RESULTS");
      }

      const formattedAddress = results[0].formatted_address;
      const latLng = await getLatLng(results[0]);
      const coverageInfo = await coverageAreaByGeocode({ ...latLng });
      const nextArea = {
        ...coverageInfo,
        address: coverageInfo.address || formattedAddress || query,
      };

      updateValue(onSelectStoreAction, [...(onSelectStore || []), nextArea]);
      updateValue(storeAction, "");
      onSelectedLocation?.(nextArea);
    } catch (error) {
      console.error("Error getting lat/lng:", error);
      showSnackbar("Google could not find that address. Check the spelling or try a nearby street.", "warning", 6000, "bottom-center");
    } finally {
      setIsResolving(false);
    }
  };

  const searcherContent = (
    <PlacesAutocomplete value={store || ""} onChange={(value) => updateValue(storeAction, value)} onSelect={(value) => applyAddress(value)}>
      {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => {
        const typedAddress = (store || "").trim();
        const showUseTypedAddress = !loading && typedAddress.length >= 8 && suggestions.length === 0;
        const showDropdown = loading || suggestions.length > 0 || showUseTypedAddress;

        return (
          <Box sx={{ position: "relative", mr: 2, ...sx }}>
            <Paper
              component="form"
              onSubmit={(e) => {
                e.preventDefault();
                applyAddress(typedAddress);
              }}
              sx={{
                mr: 2,
                p: "2px 8px",
                display: "flex",
                alignItems: "center",
                width: "100%",
                ml: "auto",
                mb: 2,
                borderRadius: 2,
                border: `1px solid ${colors.orange[200]}`,
                backgroundColor: "var(--fx-surface-base, #FFFFFF)",
                boxShadow: "0 2px 6px rgba(0,0,0,0.04)",
                "&:hover": {
                  borderColor: colors.orange[400],
                },
                "&:focus-within": {
                  borderColor: colors.orange.base,
                  boxShadow: `0 0 0 2px ${colors.orange[200]}55`,
                },
              }}
            >
              <InputBase
                {...getInputProps({ className: "location-search-input", label: "State" })}
                sx={{
                  ml: 1,
                  flex: 1,
                  fontSize: "0.95rem",
                  color: colors.gray.base,
                  "&::placeholder": {
                    color: colors.gray[300],
                    opacity: 1,
                  },
                }}
                placeholder={label}
              />
              <Divider sx={{ height: 28, m: 0.5 }} orientation="vertical" />
              <IconButton
                type="submit"
                disabled={isResolving || !typedAddress}
                sx={{
                  p: "10px",
                  color: colors.orange.base,
                  "&:hover": {
                    backgroundColor: colors.orange[50],
                  },
                }}
                aria-label="search"
              >
                <Search />
              </IconButton>
            </Paper>

            {showDropdown && (
              <div
                style={{
                  position: "absolute",
                  top: 60,
                  zIndex: 1200,
                  backgroundColor: "var(--fx-surface-base, #FFFFFF)",
                  border: `1px solid ${colors.orange[100]}`,
                  borderRadius: "10px",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                  width: "100%",
                  maxHeight: "220px",
                  overflowY: "auto",
                }}
              >
                {loading && <div style={{ padding: "8px", color: colors.gray[400] }}>Loading...</div>}
                {suggestions?.map((suggestion, idx) => {
                  const style = {
                    padding: "10px 12px",
                    cursor: "pointer",
                    fontSize: "0.9rem",
                    backgroundColor: suggestion.active ? colors.orange[50] : "var(--fx-surface-base, #FFFFFF)",
                    color: colors.gray.base,
                    borderBottom: `1px solid ${colors.gray[100]}`,
                  };

                  return (
                    <div key={idx} {...getSuggestionItemProps(suggestion, { style })}>
                      {suggestion.description}
                    </div>
                  );
                })}
                {showUseTypedAddress && (
                  <Box
                    onClick={() => applyAddress(typedAddress)}
                    sx={{
                      padding: "10px 12px",
                      cursor: "pointer",
                      "&:hover": { backgroundColor: colors.orange[50] },
                    }}
                  >
                    <Typography sx={{ fontSize: "0.9rem", color: colors.gray.base, fontWeight: 600 }}>Use this address</Typography>
                    <Typography sx={{ fontSize: "0.8rem", color: colors.gray[400] }}>{typedAddress}</Typography>
                  </Box>
                )}
              </div>
            )}
          </Box>
        );
      }}
    </PlacesAutocomplete>
  );

  return disableLoadScript ? (
    searcherContent
  ) : (
    <LoadScriptNext googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY} libraries={["places"]}>
      {searcherContent}
    </LoadScriptNext>
  );
};
